import { useNavigate, Link } from "react-router-dom";

const footerLinks = {
    product: [
        { label: "Features", href: "/info/features" },
        { label: "Pricing", href: "/info/pricing" }
    ],
    resources: [
        { label: "Documentation", href: "https://docs.dev.stockalgotrader.com", external: true },
        { label: "Support", href: "/info/support" }
    ],
    company: [
        { label: "About Us", href: "/info/about" },
        { label: "Contact", href: "/info/contact" }
    ],
    legal: [
        { label: "Privacy Policy", href: "/info/privacy" },
        { label: "Terms of Service", href: "/info/terms" },
        { label: "Risk Disclosure", href: "/info/risk" }
    ]
};

const FooterLink = ({ link }) => {
    const className = "text-sm text-base-content/60 hover:text-primary transition-colors";
    if (link.external) {
        return (
            <a href={link.href} className={className} target={link.href.startsWith('http') ? "_blank" : undefined} rel={link.href.startsWith('http') ? "noopener noreferrer" : undefined}>
                {link.label}
            </a>
        );
    }
    return (
        <Link to={link.href} className={className}>
            {link.label}
        </Link>
    );
};

const Footer = ({ className = "" }) => {
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();

    return (
        <footer className={`bg-base-200 pt-16 pb-8 px-4 md:px-12 lg:px-20 ${className}`}>
            <div className="max-w-7xl mx-auto">
                {/* Main Footer Content */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
                    {/* Brand Column */}
                    <div className="col-span-2">
                        <div
                            className="flex items-center cursor-pointer mb-4"
                            onClick={() => navigate('/')}
                        >
                            <img
                                className="w-10 h-10"
                                alt="StockAlgoTrader Logo"
                                src="/group-39795.svg"
                            />
                            <span className="ml-2 text-xl font-bold text-gradient-primary font-heading">
                                StockAlgoTrader
                            </span>
                        </div>
                        <p className="text-base-content/60 text-sm leading-relaxed mb-4">
                            AI-powered algorithmic trading platform for Indian stock markets.
                            Build, backtest, and deploy strategies for NSE & BSE.
                        </p>
                        {/* Social Links */}
                        <div className="flex gap-3">
                            <a href="#" className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-primary">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                                </svg>
                            </a>
                            <a href="#" className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-primary">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                </svg>
                            </a>
                            <a href="#" className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-primary">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Link Columns */}
                    <div>
                        <h4 className="font-bold text-base-content mb-4">Product</h4>
                        <ul className="space-y-2">
                            {footerLinks.product.map((link, i) => (
                                <li key={i}><FooterLink link={link} /></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-base-content mb-4">Resources</h4>
                        <ul className="space-y-2">
                            {footerLinks.resources.map((link, i) => (
                                <li key={i}><FooterLink link={link} /></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-base-content mb-4">Company</h4>
                        <ul className="space-y-2">
                            {footerLinks.company.map((link, i) => (
                                <li key={i}><FooterLink link={link} /></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold text-base-content mb-4">Legal</h4>
                        <ul className="space-y-2">
                            {footerLinks.legal.map((link, i) => (
                                <li key={i}><FooterLink link={link} /></li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-base-300 pt-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-sm text-base-content/50">
                            © {currentYear} StockAlgoTrader. All rights reserved.
                        </p>
                        <p className="text-xs text-base-content/40 max-w-2xl text-center md:text-right">
                            Trading involves substantial risk. Past performance is not indicative of future results.
                            This platform is for informational purposes only and not investment advice.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
